import { useEffect, useState, useMemo } from 'react'
import { useParticipantes } from './UseParticipantes'
import { ParticipantesFilters } from './ParticipantesFilters'
import { ParticipantesTable } from './ParticipantesTable'
import { ParticipanteForm } from './ParticipanteForm'
import { AsistenciasCard } from './AsistenciasCard'
import { AsistenciaForm } from './AsistenciaForm'
import { Alert, Modal } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import { useSelector } from "react-redux";
import { selectAppPermissions } from "../../../../store/auth/selectors";

export default function ParticipantesManager() {
  const {
    participantes,
    tematicas,
    instancias,
    loading,
    error,
    fetchParticipantes,
    createParticipante,
    updateParticipante,
    deleteParticipante,
    createAsistencia,
    updateAsistencia,
    deleteAsistencia
  } = useParticipantes()

  const [filters, setFilters] = useState({
    search: '',
    cohorte: '',
    activo: '',
    certificado: ''
  })

  const [showParticipanteForm, setShowParticipanteForm] = useState(false)
  const [editingParticipante, setEditingParticipante] = useState(null)
  const [showAsistenciasCard, setShowAsistenciasCard] = useState(false)
  const [selectedParticipante, setSelectedParticipante] = useState(null)
  const [showAsistenciaForm, setShowAsistenciaForm] = useState(false)

  const appName = 'curso-ciclos-vida';
  const permissions = useSelector((state) => selectAppPermissions(state, appName));
  const permissionInsert = permissions.priv_insert === 'Y';
  const permissionExport = permissions.priv_export === 'Y';
  const permissionUpdate = permissions.priv_update === 'Y';
  const permissionDelete = permissions.priv_delete === 'Y';


  useEffect(() => {
    fetchParticipantes()
  }, [fetchParticipantes])

  const filteredParticipantes = useMemo(() => {
    return participantes.filter(participante => {
      const matchesSearch = !filters.search ||
        participante.nombre_matriculado.toLowerCase().includes(filters.search.toLowerCase()) ||
        participante.identificacion.includes(filters.search) ||
        participante.email.toLowerCase().includes(filters.search.toLowerCase())
      const matchesCohorte = !filters.cohorte ||
        participante.cohorte.toString() === filters.cohorte
      const matchesActivo = !filters.activo ||
        participante.activo === filters.activo
      const matchesCertificado = !filters.certificado ||
        participante.certificado === filters.certificado
      return matchesSearch && matchesCohorte && matchesActivo && matchesCertificado
    })
  }, [participantes, filters])

  const handleCreateParticipante = () => {
    setEditingParticipante(null)
    setShowParticipanteForm(true)
  }

  const handleEditParticipante = (participante) => {
    setEditingParticipante(participante)
    setShowParticipanteForm(true)
  }

  const handleSaveParticipante = async (participanteData) => {
    if (editingParticipante) {
      await updateParticipante(editingParticipante.oid, participanteData)
    } else {
      await createParticipante(participanteData)
    }
    setShowParticipanteForm(false)
    setEditingParticipante(null)
  }

  const handleDeleteParticipante = async (oid) => {
    Modal.confirm({
      title: '¿Estás seguro de que deseas eliminar este participante?',
      content: 'Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await deleteParticipante(oid);
        } catch (error) {
          console.error('Error eliminando participante:', error);
        }
      }
    });
  };
  const handleViewAsistencias = (participante) => {
    setSelectedParticipante(participante)
    setShowAsistenciasCard(true)
  }

  const handleAssignAsistencia = (participante) => {
    setSelectedParticipante(participante)
    setShowAsistenciaForm(true)
  }

  const handleSaveAsistencia = async (asistenciaData) => {
    await createAsistencia(asistenciaData)
    setShowAsistenciaForm(false)
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      {error && (
        <Alert
          type="error"
          message={
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faExclamationCircle} />
              <span>{error}</span>
            </div>
          }
          showIcon
        />
      )}

      <ParticipantesFilters
        onFilterChange={setFilters}
        onCreateClick={handleCreateParticipante}
        filteredParticipantes={filteredParticipantes}
        loading={loading}
        permissionInsert={permissionInsert}
        permissionExport={permissionExport}
      />

      <ParticipantesTable
        participantes={filteredParticipantes}
        loading={loading}
        onEdit={handleEditParticipante}
        onDelete={handleDeleteParticipante}
        onViewAsistencias={handleViewAsistencias}
        onAssignAsistencia={handleAssignAsistencia}
        permissionInsert={permissionInsert}
        permissionDelete={permissionDelete}
        permissionUpdate={permissionUpdate}
      />
      <ParticipanteForm
        open={showParticipanteForm}
        onClose={() => {
          setShowParticipanteForm(false)
          setEditingParticipante(null)
        }}
        onSave={handleSaveParticipante}
        participante={editingParticipante}
        instancias={instancias}
        loading={loading}
      />
      <AsistenciasCard
        open={showAsistenciasCard}
        onClose={() => {
          setShowAsistenciasCard(false)
          setSelectedParticipante(null)
        }}
        participanteId={selectedParticipante?.oid}
        participantes={participantes}
        tematicas={tematicas}
        onCreateAsistencia={createAsistencia}
        onUpdateAsistencia={updateAsistencia}
        onDeleteAsistencia={deleteAsistencia}
        loading={loading}
        permissionInsert={permissionInsert}
        permissionDelete={permissionDelete}
        permissionUpdate={permissionUpdate}
      />
      {selectedParticipante && (
        <AsistenciaForm
          open={showAsistenciaForm}
          onClose={() => {
            setShowAsistenciaForm(false)
            setSelectedParticipante(null)
          }}
          onSave={handleSaveAsistencia}
          participante={selectedParticipante}
          tematicas={tematicas}
          loading={loading}
        />
      )}
    </div>
  )
}
